"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.RushOpportunityService = void 0;
exports.mapDispositionToOutcome = mapDispositionToOutcome;
const browser_1 = require("convex/browser");
const environment_1 = require("@environment/environment");
// String function references — this repo has no Convex codegen so we bypass
// the FunctionReference<T> typing by casting to `any` at the call sites.
const FN = {
    list: 'calls/missedCalls/logic:listRushOpportunitiesForAgent',
    claim: 'calls/missedCalls/logic:claimRushOpportunity',
    release: 'calls/missedCalls/logic:releaseRushOpportunity',
    resolve: 'calls/missedCalls/logic:resolveRushOpportunity',
    // Flips a claimed opp to `in_call` once the lead picks up. After this the
    // stale-claim cron in Convex can no longer release it, so the opp is safe
    // for arbitrarily long conversations.
    markInCall: 'calls/missedCalls/logic:markRushOpportunityInCall',
    // Dev-only seed mutation (in convex/tmp/rush/seed.ts).
    seed: 'tmp/rush/seed:seedRushOpportunities',
};
let client = null;
function getClient() {
    var _a;
    if (!environment_1.environment.rush || !environment_1.environment.rush.enabled) {
        console.warn('[RushSvc] disabled: environment.rush.enabled =', (_a = environment_1.environment.rush) === null || _a === void 0 ? void 0 : _a.enabled);
        return null;
    }
    if (!environment_1.environment.rush.convexUrl || environment_1.environment.rush.convexUrl.indexOf('CHANGE_ME') === 0) {
        console.warn('[RushSvc] disabled: invalid convexUrl =', environment_1.environment.rush.convexUrl);
        return null;
    }
    if (!client) {
        console.log('[RushSvc] creating ConvexClient ->', environment_1.environment.rush.convexUrl);
        client = new browser_1.ConvexClient(environment_1.environment.rush.convexUrl);
    }
    return client;
}
function identityArgs(id) {
    const out = {};
    if (id.externalId)
        out.agentExternalId = id.externalId;
    if (id.email)
        out.agentEmail = id.email;
    return out;
}
exports.RushOpportunityService = {
    isEnabled() {
        return getClient() !== null;
    },
    /**
     * Subscribes to the live list of opportunities for this agent. Returns an
     * unsubscribe function. The callback is invoked every time the list changes.
     */
    watch(identity, onUpdate, onError) {
        const c = getClient();
        if (!c)
            return () => undefined;
        try {
            return c.onUpdate(FN.list, Object.assign({ apiKey: environment_1.environment.rush.apiKey }, identityArgs(identity)), (items) => onUpdate(items || []), (err) => onError && onError(err));
        }
        catch (err) {
            if (onError)
                onError(err);
            return () => undefined;
        }
    },
    claim(identity, opportunityId) {
        return __awaiter(this, void 0, void 0, function* () {
            const c = getClient();
            if (!c)
                return { ok: false, reason: 'disabled' };
            try {
                const res = yield c.mutation(FN.claim, Object.assign(Object.assign({ apiKey: environment_1.environment.rush.apiKey }, identityArgs(identity)), { opportunityId }));
                // Backend returns { id, claimExpiresAt, fromNumber, toNumber } on success.
                // Normalize to { ok, opportunity } so the controller can dial.
                const r = res;
                if (r && r.id) {
                    return {
                        ok: true,
                        opportunity: {
                            _id: r.id,
                            fromNumber: r.fromNumber,
                            toNumber: r.toNumber,
                        },
                    };
                }
                return { ok: false, reason: 'unknown_response' };
            }
            catch (err) {
                console.error('[Rush] claim failed', err);
                // ConvexError surfaces the original payload as `err.data` (object form).
                // Detect AGENT_HAS_ACTIVE_CLAIM so the FE can offer to release & retry.
                const data = err === null || err === void 0 ? void 0 : err.data;
                if (data && typeof data === 'object') {
                    if (data.code === 'AGENT_HAS_ACTIVE_CLAIM') {
                        return {
                            ok: false,
                            reason: 'AGENT_HAS_ACTIVE_CLAIM',
                            existingClaimId: data.existingClaimId,
                        };
                    }
                    if (typeof data.code === 'string') {
                        return { ok: false, reason: data.code };
                    }
                }
                return { ok: false, reason: 'error' };
            }
        });
    },
    release(identity, opportunityId) {
        return __awaiter(this, void 0, void 0, function* () {
            const c = getClient();
            if (!c)
                return;
            try {
                yield c.mutation(FN.release, Object.assign(Object.assign({ apiKey: environment_1.environment.rush.apiKey }, identityArgs(identity)), { opportunityId }));
            }
            catch (err) {
                console.error('[Rush] release failed', err);
            }
        });
    },
    resolve(identity, opportunityId, outcome) {
        return __awaiter(this, void 0, void 0, function* () {
            const c = getClient();
            if (!c)
                return;
            try {
                yield c.mutation(FN.resolve, Object.assign(Object.assign({ apiKey: environment_1.environment.rush.apiKey }, identityArgs(identity)), { opportunityId,
                    outcome }));
            }
            catch (err) {
                console.error('[Rush] resolve failed', err);
            }
        });
    },
    /**
     * Mark a claimed opportunity as `in_call` (lead picked up). Called from
     * `onCallAnswered`. Idempotent on the server: safe to call twice (e.g.
     * if the SDK re-fires the answered event). Failures are swallowed because
     * the call is already live — we don't want to break the conversation if
     * Convex is briefly unreachable; the cron + 90s TTL won't touch this doc
     * because it transitions to `in_call` server-side, but if this call never
     * lands the cron will eventually clean up the orphan claim.
     */
    markAnswered(identity, opportunityId) {
        return __awaiter(this, void 0, void 0, function* () {
            const c = getClient();
            if (!c)
                return;
            try {
                yield c.mutation(FN.markInCall, Object.assign(Object.assign({ apiKey: environment_1.environment.rush.apiKey }, identityArgs(identity)), { opportunityId }));
            }
            catch (err) {
                console.error('[Rush] markAnswered failed', err);
            }
        });
    },
    /**
     * Dev-only: seed Rush opportunities for the current agent. Calls the
     * `tmp/rush/seed:seedRushOpportunities` mutation. Returns the raw response
     * so the caller can show feedback. Throws on error so the controller can
     * surface it to the user.
     */
    seed(identity_1) {
        return __awaiter(this, arguments, void 0, function* (identity, count = 3) {
            const c = getClient();
            if (!c)
                throw new Error('Rush service disabled');
            const args = { count };
            if (identity.email)
                args.email = identity.email;
            if (identity.externalId)
                args.agentExternalId = identity.externalId;
            return yield c.mutation(FN.seed, args);
        });
    },
};
/**
 * Maps a legacy disposition (root + selected) to a Rush outcome. Anything not
 * mapped falls back to `no_answer`. Adjust as new dispositions appear.
 */
function mapDispositionToOutcome(rootDisposition, selectedDisposition) {
    const root = String(rootDisposition || '').trim().toLowerCase();
    const sel = String(selectedDisposition || '').trim().toLowerCase();
    if (root === 'application submitted')
        return 'reached';
    if (sel.indexOf('voicemail') >= 0 || sel.indexOf('vm') === 0)
        return 'voicemail';
    if (sel.indexOf('wrong number') >= 0 || sel.indexOf('wrong_number') >= 0)
        return 'wrong_number';
    if (sel.indexOf('do not call') >= 0 || sel.indexOf('dnc') >= 0)
        return 'do_not_call';
    if (sel.indexOf('no answer') >= 0 || sel.indexOf('no_answer') >= 0)
        return 'no_answer';
    if (root === 'contacted' || sel.indexOf('contacted') >= 0)
        return 'reached';
    return 'no_answer';
}
